/**
 * Request Transformer
 * Provides:
 *  1. camelCase → PascalCase recursive key conversion for C# backend compatibility.
 *  2. Metadata envelope wrapping ({ metadata, ...data }).
 *  3. Auth header generation (X-Tenant-ID, Authorization: Bearer).
 *
 * Used by BaseHttpClient before sending any mutating request.
 */

import { atlasApiConfig } from './config';
import { createGuid } from '@bizfirst/common-js';

// ─── PascalCase conversion ────────────────────────────────────────────────────

/**
 * Capitalise the first character of a string (camelCase → PascalCase for keys).
 */
export const toPascalCase = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Recursively convert every object key from camelCase to PascalCase.
 * Arrays and primitive values pass through unchanged.
 * Keys that already start with an uppercase letter are unchanged.
 */
export const convertKeysToPascalCase = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToPascalCase(item));
  }

  if (typeof obj === 'object' && obj !== null && obj.constructor === Object) {
    const converted: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      converted[toPascalCase(key)] = convertKeysToPascalCase(
        (obj as Record<string, unknown>)[key]
      );
    }
    return converted;
  }

  return obj;
};

// ─── Request metadata envelope ───────────────────────────────────────────────

/**
 * Wrap a request payload with the standard metadata envelope expected by the
 * C# backend.  Keys in `data` are recursively PascalCase-converted.
 *
 * @param data     The request payload (camelCase or PascalCase).
 * @param nestData When true the converted payload is placed under a `data`
 *                 key.  When false (default) it is spread into the root.
 */
export const wrapRequest = <T>(data: T, nestData = false): unknown => {
  const metadata = {
    RequestId: createGuid(),
    CorrelationId: createGuid(),
    Timestamp: new Date().toISOString(),
    Version: '1.0',
  };

  const converted = convertKeysToPascalCase(data);

  if (nestData) {
    return { metadata, data: converted };
  }

  if (converted && typeof converted === 'object' && !Array.isArray(converted)) {
    return { metadata, ...(converted as Record<string, unknown>) };
  }

  return { metadata, data: converted };
};

// ─── Auth headers ─────────────────────────────────────────────────────────────

/**
 * Build authentication headers from the current atlasApiConfig.
 * Includes X-Tenant-ID and Authorization: Bearer when values are present.
 */
export const buildAuthHeaders = (
  overrideTenantId?: number,
  overrideToken?: string
): Record<string, string> => {
  const headers: Record<string, string> = {};

  const tenantId = overrideTenantId ?? atlasApiConfig.tenantId;
  if (tenantId) {
    headers['X-Tenant-ID'] = String(tenantId);
  }

  const token = overrideToken ?? atlasApiConfig.token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};
